/**
 * URL Serializer - Share searches and results via URL
 *
 * Provides functionality to:
 * - Serialize search parameters to URL
 * - Deserialize URL to restore search state
 * - Generate shareable links
 * - Support deep linking
 */

export class URLSerializer {
    constructor() {
        this.maxObjectsInUrl = 10; // Limit objects in URL to prevent excessive length
    }

    /**
     * Serialize search parameters to URL
     * @param {Object} searchParams - Search parameters
     * @param {Array} objects - Optional: Top objects to include
     * @returns {string} Full URL with parameters
     */
    serializeToURL(searchParams, objects = null) {
        const params = new URLSearchParams();

        // Location parameters
        if (searchParams.latitude !== undefined) {
            params.set('lat', searchParams.latitude.toFixed(4));
        }
        if (searchParams.longitude !== undefined) {
            params.set('lon', searchParams.longitude.toFixed(4));
        }

        // Date parameter
        if (searchParams.observationDate) {
            params.set('date', searchParams.observationDate);
        }

        // Direction parameters
        if (searchParams.azimuth !== undefined) {
            params.set('az', searchParams.azimuth.toFixed(1));
        }
        if (searchParams.minAltitude !== undefined) {
            params.set('minAlt', searchParams.minAltitude.toFixed(0));
        }
        if (searchParams.maxAltitude !== undefined) {
            params.set('maxAlt', searchParams.maxAltitude.toFixed(0));
        }
        if (searchParams.fieldOfView !== undefined) {
            params.set('fov', searchParams.fieldOfView.toFixed(0));
        }

        // Filter parameters
        if (searchParams.minMagnitude !== undefined) {
            params.set('minMag', searchParams.minMagnitude.toFixed(1));
        }
        if (searchParams.maxMagnitude !== undefined) {
            params.set('maxMag', searchParams.maxMagnitude.toFixed(1));
        }
        if (searchParams.minDuration !== undefined) {
            params.set('minDur', searchParams.minDuration.toFixed(1));
        }
        if (searchParams.moonAvoidance !== undefined) {
            params.set('moonAvoid', searchParams.moonAvoidance.toFixed(0));
        }

        // Add compressed object list if provided
        if (objects && objects.length > 0) {
            const objectNames = objects
                .slice(0, this.maxObjectsInUrl)
                .map(obj => obj.name || obj.designation)
                .join(',');
            params.set('objs', objectNames);
        }

        // Add source identifier for tracking
        params.set('source', 'share');

        // Build full URL
        const baseUrl = window.location.origin + window.location.pathname;
        return `${baseUrl}?${params.toString()}`;
    }

    /**
     * Deserialize URL parameters to search parameters
     * @returns {Object|null} Search parameters or null if no params
     */
    deserializeFromURL() {
        const params = new URLSearchParams(window.location.search);

        // Check if there are any parameters
        if (!params.has('lat') && !params.has('az')) {
            return null;
        }

        const searchParams = {};

        // Location parameters
        if (params.has('lat')) {
            searchParams.latitude = parseFloat(params.get('lat'));
        }
        if (params.has('lon')) {
            searchParams.longitude = parseFloat(params.get('lon'));
        }

        // Date parameter
        if (params.has('date')) {
            searchParams.observationDate = params.get('date');
        }

        // Direction parameters
        if (params.has('az')) {
            searchParams.azimuth = parseFloat(params.get('az'));
        }
        if (params.has('minAlt')) {
            searchParams.minAltitude = parseFloat(params.get('minAlt'));
        }
        if (params.has('maxAlt')) {
            searchParams.maxAltitude = parseFloat(params.get('maxAlt'));
        }
        if (params.has('fov')) {
            searchParams.fieldOfView = parseFloat(params.get('fov'));
        }

        // Filter parameters
        if (params.has('minMag')) {
            searchParams.minMagnitude = parseFloat(params.get('minMag'));
        }
        if (params.has('maxMag')) {
            searchParams.maxMagnitude = parseFloat(params.get('maxMag'));
        }
        if (params.has('minDur')) {
            searchParams.minDuration = parseFloat(params.get('minDur'));
        }
        if (params.has('moonAvoid')) {
            searchParams.moonAvoidance = parseFloat(params.get('moonAvoid'));
        }

        // Get object list if present
        if (params.has('objs')) {
            searchParams.objectNames = params.get('objs').split(',');
        }

        // Check if this is from a share link
        if (params.has('source')) {
            searchParams.isSharedLink = params.get('source') === 'share';
        }

        return searchParams;
    }

    /**
     * Update current URL with parameters (without page reload)
     * @param {Object} searchParams - Search parameters
     * @param {Array} objects - Optional: Top objects
     */
    updateURL(searchParams, objects = null) {
        const url = this.serializeToURL(searchParams, objects);
        window.history.pushState({ searchParams }, '', url);
    }

    /**
     * Clear URL parameters
     */
    clearURL() {
        const baseUrl = window.location.origin + window.location.pathname;
        window.history.pushState({}, '', baseUrl);
    }

    /**
     * Copy URL to clipboard
     * @param {string} url - URL to copy
     * @returns {Promise<boolean>} Success status
     */
    async copyToClipboard(url) {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);
                return true;
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = url;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                return successful;
            }
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            return false;
        }
    }

    /**
     * Validate deserialized parameters
     * @param {Object} params - Parameters to validate
     * @returns {Object} Validation result
     */
    validateParameters(params) {
        const errors = [];

        if (params.latitude !== undefined) {
            if (isNaN(params.latitude) || params.latitude < -90 || params.latitude > 90) {
                errors.push('Invalid latitude in URL');
            }
        }

        if (params.longitude !== undefined) {
            if (isNaN(params.longitude) || params.longitude < -180 || params.longitude > 180) {
                errors.push('Invalid longitude in URL');
            }
        }

        if (params.azimuth !== undefined) {
            if (isNaN(params.azimuth) || params.azimuth < 0 || params.azimuth > 360) {
                errors.push('Invalid azimuth in URL');
            }
        }

        if (params.minAltitude !== undefined && params.maxAltitude !== undefined) {
            if (params.minAltitude >= params.maxAltitude) {
                errors.push('Invalid altitude range in URL');
            }
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Get a short description of the search from URL parameters
     * @param {Object} params - Search parameters
     * @returns {string} Description
     */
    getSearchDescription(params) {
        if (!params) return 'No search parameters';

        let description = '';

        if (params.azimuth !== undefined) {
            const direction = this.azimuthToDirection(params.azimuth);
            description += `${direction} (${params.azimuth}°)`;
        }

        if (params.minAltitude !== undefined && params.maxAltitude !== undefined) {
            description += ` at ${params.minAltitude}-${params.maxAltitude}° altitude`;
        }

        if (params.observationDate) {
            description += ` on ${params.observationDate}`;
        }

        return description || 'Search parameters';
    }

    /**
     * Convert azimuth to cardinal direction
     * @param {number} azimuth - Azimuth in degrees
     * @returns {string} Cardinal direction
     */
    azimuthToDirection(azimuth) {
        if (azimuth >= 337.5 || azimuth < 22.5) return 'North';
        if (azimuth >= 22.5 && azimuth < 67.5) return 'NE';
        if (azimuth >= 67.5 && azimuth < 112.5) return 'East';
        if (azimuth >= 112.5 && azimuth < 157.5) return 'SE';
        if (azimuth >= 157.5 && azimuth < 202.5) return 'South';
        if (azimuth >= 202.5 && azimuth < 247.5) return 'SW';
        if (azimuth >= 247.5 && azimuth < 292.5) return 'West';
        if (azimuth >= 292.5 && azimuth < 337.5) return 'NW';
        return 'Unknown';
    }
}

export default URLSerializer;
